import { FC } from 'react';
import { Dropdown, Menu } from 'antd';
import { TbLayoutGrid, TbLock } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import {
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemoStore,
    usePipelineStore,
    useWorkspaceStore,
} from '@/stores';
import { ResourceType } from '@/stores/database/entities/00-RBAC/99-Collaborators/types';
import {
    DashboardVisibility,
    FolderVisibilityType,
    InvestmentVisibilityType,
    ListVisibility,
    MemoType,
    PipelineVisibilityType,
} from '@/types';

import type { InviteableResourceVisibility, InviteableWorkspaceResource } from '../../../../types';

interface VisibilityDropdownProps {
    onVisibilityChange?: (visibility: InviteableResourceVisibility) => Promise<void> | void;
    resource: InviteableWorkspaceResource;
    resourceType: ResourceType;
}

const VisibilityDropdown: FC<VisibilityDropdownProps> = ({
    onVisibilityChange,
    resource,
    resourceType,
}): JSX.Element => {
    const { selectedWorkspace: workspace } = useWorkspaceStore();

    const { updateFolder } = useFolderStore();
    const { updateInvestment } = useInvestmentStore();
    const { updateMemo } = useMemoStore();
    const { updatePipeline } = usePipelineStore();
    const { updateDashboard } = useDashboardStore();
    const { updateList } = useListStore();

    const workspaceResourceVisibilityMap: Record<InviteableResourceVisibility, JSX.Element> = {
        Private: <TbLock size='1rem' style={{ verticalAlign: '-0.175rem' }} />,
        Workspace: <TbLayoutGrid size='1rem' style={{ verticalAlign: '-0.175rem' }} />,
    };

    const handleVisibilityChange = (visibility: InviteableResourceVisibility) => {
        if (onVisibilityChange) {
            void onVisibilityChange(visibility);
            return;
        }

        switch (resourceType) {
            case 'folders':
                updateFolder(resource.id, { visibility: visibility as FolderVisibilityType });
                break;
            case 'investments':
                updateInvestment(resource.id, { visibility: visibility as InvestmentVisibilityType });
                break;
            case 'memos':
                updateMemo(resource.id, { type: visibility as MemoType });
                break;
            case 'pipelines':
                updatePipeline(resource.id, { visibility: visibility as PipelineVisibilityType });
                break;
            case 'dashboards':
                updateDashboard(resource.id, { visibility: visibility as DashboardVisibility });
                break;
            case 'lists':
                updateList(resource.id, { visibility: visibility as ListVisibility });
                break;
            case 'lifecycles':
                break;
        }
    };

    const { token } = useAntDesignTheme();

    return (
        <>
            <Dropdown
                placement='bottomLeft'
                trigger={['click']}
                popupRender={() => (
                    <DropdownMenuPanel
                        selectedKeys={[resource?.visibility ?? resource?.type ?? 'Private']}
                        onClick={(e) => {
                            handleVisibilityChange(e.key as InviteableResourceVisibility);
                        }}
                    >
                        <div style={{ padding: '0 0.75rem', marginBottom: '0.325rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>Resource Visibility</small>
                        </div>
                        <Menu.Item key={'Private'}>
                            <TbLock size='1rem' style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />
                            Private
                            <br />
                            <small style={{ color: token.colorTextSecondary }}>
                                Only members invited to this resource can access it.
                            </small>
                        </Menu.Item>
                        <Menu.Item key={'Workspace'}>
                            <TbLayoutGrid size='1rem' style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />
                            Workspace
                            <br />
                            <small style={{ color: token.colorTextSecondary }}>
                                Members within <strong>{workspace?.name}</strong> can access this resource.
                            </small>
                        </Menu.Item>
                    </DropdownMenuPanel>
                )}
                overlayStyle={{ minWidth: 200 }}
            >
                <DropdownButton
                    icon={
                        workspaceResourceVisibilityMap[
                            (resource.visibility ?? resource.type ?? 'Private') as InviteableResourceVisibility
                        ]
                    }
                >
                    {resource.visibility ?? resource.type ?? 'Private'}
                </DropdownButton>
            </Dropdown>
        </>
    );
};

export default VisibilityDropdown;
